import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import { collection, getDocs, query, orderBy } from "firebase/firestore"
import { db } from "../firebase/firebase"
import { Order } from "../model/OldOrderModel"

class OrderReportService {

  /* ================================
     FETCH ALL ORDERS (FILTERED BY BRANCH)
  ================================= */
  async fetchAllOrders(branchId?: string): Promise<Order[]> {
    try {
      console.log("🔍 Fetching orders from Firestore...")
      console.log("📍 Branch ID filter:", branchId || "None (fetching all)")

      const q = query(
        collection(db, "orders"),
        orderBy("orderPlaceDate", "desc")
      )

      const snapshot = await getDocs(q)
      console.log("📦 Total orders fetched from Firestore:", snapshot.docs.length)

      let orders: Order[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
      } as unknown as Order))

      console.log("📋 Sample order data:", orders[0])

      // Filter by branch if branchId is provided
      if (branchId) {
        console.log("🔎 Filtering orders by branch ID:", branchId)
        
        const beforeFilter = orders.length
        orders = orders.filter(order => {
          const matches = order.xone === branchId
          if (!matches && orders.indexOf(order) < 3) {
            console.log(`❌ Order ${order.orderNumber} - xone: "${order.xone}" does not match "${branchId}"`)
          }
          return matches
        })
        
        console.log(`✅ Filtered: ${beforeFilter} → ${orders.length} orders (${orders.length} match the branch)`)
        
        if (orders.length === 0) {
          console.warn("⚠️ No orders found for this branch!")
          console.log("💡 Check if xone field exists and matches:", branchId)
          console.log("📊 Sample xone values from first 5 orders:")
          snapshot.docs.slice(0, 5).forEach((doc, i) => {
            const data = doc.data()
            console.log(`  Order ${i + 1}: xone = "${data.xone}"`)
          })
        }
      } else {
        console.log("ℹ️ No branch filter applied, returning all orders")
      }

      console.log("✨ Final orders count:", orders.length)
      return orders
    } catch (error) {
      console.error("❌ Failed to fetch orders:", error)
      return []
    }
  }

  /* ================================
     FILTER MONTHLY ORDERS
  ================================= */
  filterMonthlyOrders(
    orders: Order[],
    month: number, // 1 = Jan
    year: number
  ): Order[] {
    return orders.filter(order => {
      // Handle both timestamp (milliseconds) and Firestore Timestamp objects
      let orderDate: Date
      
      if (order.orderPlaceDate && typeof order.orderPlaceDate === 'object' && 'toDate' in order.orderPlaceDate) {
        // Firestore Timestamp object
        orderDate = (order.orderPlaceDate as any).toDate()
      } else if (typeof order.orderPlaceDate === 'number') {
        // Unix timestamp in milliseconds
        orderDate = new Date(order.orderPlaceDate)
      } else {
        // Invalid date
        console.warn("Invalid orderPlaceDate format:", order.orderPlaceDate)
        return false
      }
      
      return (
        orderDate.getMonth() + 1 === month &&
        orderDate.getFullYear() === year
      )
    })
  }

  /* ================================
     EXPORT TO EXCEL
  ================================= */
  exportToExcel(orders: Order[], month: number, year: number) {
    const sheetData = orders.map(o => {
      // Handle date conversion
      let orderDate: Date
      if (o.orderPlaceDate && typeof o.orderPlaceDate === 'object' && 'toDate' in o.orderPlaceDate) {
        orderDate = (o.orderPlaceDate as any).toDate()
      } else {
        orderDate = new Date(o.orderPlaceDate)
      }
      
      return {
        "Order No": o.orderNumber,
        "Customer Name": o.customerName,
        "Customer Number": o.customerNumber,
        "Store": o.customerStore,
        "Status": o.status,
        "Payment": o.payment,
        "Payment Status": o.paymentStatus,
        "Total Price": o.totalPrice,
        "Delivery Charge": o.deliveryCharge,
        "Packing": o.packing || "0",
        "Tax": o.tax || "0",
        "Service": o.service || "0",
        "Order Date": orderDate.toLocaleString()
      }
    })

    const worksheet = XLSX.utils.json_to_sheet(sheetData)
    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Monthly Orders"
    )

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    })

    const blob = new Blob(
      [excelBuffer],
      { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
    )

    saveAs(blob, `ZAPEE_Orders_${month}_${year}.xlsx`)
  }
}

export default new OrderReportService()